import { useCallback, useEffect, useState } from 'react';
import { MessagingService } from '../services';
import type {
  ChatConfig,
  Conversation,
  ConversationHookResult,
  ConversationStatus,
} from '../types';
import { generateUUID } from '../utils';
import { useMessaging } from './useMessaging';
import { useStorage } from './useStorage';

export interface ConversationOptions {
  config: ChatConfig;
  autoRestore?: boolean; // Whether to automatically restore previous conversation on mount
}

/**
 * Custom hook for managing conversation state and lifecycle
 *
 * Follows the exact same flow as the reference implementation:
 * New Conversation: getUnauthenticatedAccessToken → createConversation → Subscribe to EventSource
 * Existing Conversation: getContinuityJwt → listConversations → listConversationEntries → Subscribe to EventSource
 *
 * @param options - Configuration options for the conversation
 * @returns Conversation management functions and state
 */
export function useConversation(
  options: ConversationOptions
): ConversationHookResult {
  const { config, autoRestore = true } = options;
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [status, setStatus] = useState<ConversationStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isExistingConversation, setIsExistingConversation] = useState(false);

  const storage = useStorage({
    type: 'localStorage',
    prefix: 'salesforce_messaging',
    defaultExpiration: 24 * 60 * 60 * 1000, // 24 hours default
  });

  const messaging = useMessaging({ config });

  /**
   * Handle new conversation flow
   * 1. Fetch an Unauthenticated Access Token (JWT)
   * 2. Create a new conversation
   */
  const handleNewConversation = useCallback(
    async (routingAttributes?: Record<string, string>): Promise<string> => {
      const messagingService = new MessagingService(config);

      // 1. Get unauthenticated access token
      const tokenResponse =
        await messagingService.getUnauthenticatedAccessToken();

      // Store JWT and configuration
      storage.setItem('jwt', tokenResponse.accessToken);
      storage.setItem('lastEventId', tokenResponse.lastEventId);
      if (tokenResponse.context && tokenResponse.context.configuration) {
        storage.setItem(
          'deploymentConfiguration',
          tokenResponse.context.configuration
        );
      }

      // 2. Generate conversation ID and create conversation
      const newConversationId = generateUUID();
      setConversationId(newConversationId);
      storage.setItem('conversationId', newConversationId);

      await messagingService.createConversation(
        newConversationId,
        routingAttributes
      );

      return newConversationId;
    },
    [config, storage]
  );

  /**
   * Handle existing conversation flow
   * 1. Fetch a Continuation Access Token (JWT)
   * 2. List conversations and load the current one
   * 3. Fetch conversation entries
   */
  const handleExistingConversation = useCallback(async (): Promise<
    string | null
  > => {
    const messagingService = new MessagingService(config);

    try {
      // 1. Get continuation JWT
      const tokenResponse = await messagingService.getContinuityJwt();
      storage.setItem('jwt', tokenResponse.accessToken);

      // 2. List conversations
      const conversationsResponse = await messagingService.listConversations();

      if (
        conversationsResponse.openConversationsFound > 0 &&
        conversationsResponse.conversations.length
      ) {
        const openConversations = conversationsResponse.conversations;

        if (openConversations.length > 1) {
          console.warn(
            `Expected 1 open conversation but found ${openConversations.length}. Loading the most recent.`
          );
          openConversations.sort((a, b) => b.startTimestamp - a.startTimestamp);
        }

        const currentConversationId = openConversations[0]?.conversationId;
        if (!currentConversationId) {
          return null;
        }

        setConversationId(currentConversationId);
        storage.setItem('conversationId', currentConversationId);

        // 3. Load conversation entries
        const entries = await messagingService.listConversationEntries(
          currentConversationId
        );

        // Process entries and add them to messaging
        // This would typically involve transforming the entries and adding them to the message list
        // eslint-disable-next-line no-console
        console.log('Loaded conversation entries:', entries);

        return currentConversationId;
      }
      // No open conversations found
      return null;
    } catch (err) {
      console.error('Failed to handle existing conversation:', err);
      throw err;
    }
  }, [config, storage]);

  const startConversation = useCallback(
    async (
      routingAttributes?: Record<string, string>
    ): Promise<Conversation> => {
      setStatus('connecting');
      setError(null);

      try {
        let finalConversationId: string;

        // Check if we have an existing JWT
        const existingJwt = storage.getItem<string>('jwt');

        if (existingJwt && isExistingConversation) {
          // Handle existing conversation flow
          const existingConvId = await handleExistingConversation();
          if (existingConvId) {
            finalConversationId = existingConvId;
          } else {
            // No existing conversation, create new one
            finalConversationId = await handleNewConversation(
              routingAttributes
            );
          }
        } else {
          // Handle new conversation flow
          finalConversationId = await handleNewConversation(routingAttributes);
        }

        // Create conversation object
        const newConversation: Conversation = {
          id: finalConversationId,
          status: 'active',
          createdAt: new Date(),
          updatedAt: new Date(),
          participants: [], // Initialize with empty participants array
        };

        setConversation(newConversation);
        setStatus('active');

        // Store conversation for session continuity
        storage.setItem('conversation', newConversation);

        // Connect messaging for real-time updates
        messaging.connect(finalConversationId);

        return newConversation;
      } catch (err) {
        const chatError =
          err instanceof Error
            ? err
            : new Error('Failed to start conversation');
        setError(chatError);
        setStatus('error');
        throw chatError;
      }
    },
    [
      storage,
      messaging,
      isExistingConversation,
      handleNewConversation,
      handleExistingConversation,
    ]
  );

  const endConversation = useCallback(async (): Promise<void> => {
    if (!conversation) {
      return;
    }

    setStatus('connecting');
    setError(null);

    try {
      const messagingService = new MessagingService(config);
      await messagingService.endConversation(conversation.id);

      // Disconnect messaging
      messaging.disconnect();

      // Clear conversation
      setConversation(null);
      setStatus('closed');

      // Remove stored conversation
      storage.removeItem('conversation');
    } catch (err) {
      const chatError =
        err instanceof Error ? err : new Error('Failed to end conversation');
      setError(chatError);
      setStatus('error');
      throw chatError;
    }
  }, [conversation, config, messaging, storage]);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Auto-restore conversation on mount - following reference implementation pattern
  useEffect(() => {
    if (autoRestore && status === 'idle') {
      const storedJwt = storage.getItem<string>('jwt');
      const storedConversationId = storage.getItem<string>('conversationId');

      if (storedJwt) {
        setIsExistingConversation(true);

        if (storedConversationId) {
          // We have both JWT and conversation ID - this is an existing conversation
          setConversationId(storedConversationId);

          // Create conversation object for existing conversation
          const existingConversation: Conversation = {
            id: storedConversationId,
            status: 'active',
            createdAt: new Date(), // We don't have the original date
            updatedAt: new Date(),
            participants: [], // Initialize with empty participants array
          };

          setConversation(existingConversation);
          setStatus('active');

          // Reconnect messaging
          messaging.connect(storedConversationId);
        }
      } else {
        // No JWT found - this will be a new conversation
        setIsExistingConversation(false);
      }
    }
  }, [autoRestore, status, storage, messaging]);

  // Handle messaging connection status changes
  useEffect(() => {
    if (conversation && status === 'active' && !messaging.isConnected) {
      // Try to reconnect if we have an active conversation but messaging is disconnected
      const timeoutId = setTimeout(() => {
        messaging.connect(conversation.id);
      }, 1000); // Brief delay before reconnecting

      return () => {
        clearTimeout(timeoutId);
      };
    }

    // Return undefined explicitly when condition is not met
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, status, messaging.isConnected, messaging.connect]);

  return {
    conversationId: conversationId || conversation?.id || null,
    messages: messaging.messages,
    status,
    error,
    isLoading: status === 'connecting',
    isConnected: messaging.isConnected,
    isConnecting: status === 'connecting',
    typingParticipants: messaging.participantTyping,
    startConversation: async (routingAttributes?: Record<string, string>) => {
      await startConversation(routingAttributes);
    },
    endConversation,
    sendMessage: messaging.sendMessage,
    startTyping: () => messaging.setTyping(true),
    stopTyping: () => messaging.setTyping(false),
    clearError,
  };
}
